/**
 * Affiliate attribution carried by the landing URL:
 *
 *   ?ref=<affiliate campaign code>&clickId=<the tracker's click id>
 *
 * Both values exist only on the URL the visitor lands on, while the account is
 * created later (often on another visit), so they are captured at boot and
 * kept until registration sends them to the backend.
 */
export interface Attribution {
  /** Affiliate campaign code (`ref`). */
  ref: string | null;
  /** The affiliate tracker's click id (`clickId`). */
  clickId: string | null;
}

const STORAGE_KEY = 'gp_attribution';
/** How long a click keeps counting — a common affiliate attribution window. */
const TTL_MS = 30 * 24 * 60 * 60 * 1000;
/** Mirrors the backend limit; anything longer is not a real click id. */
const MAX_LENGTH = 255;

interface StoredAttribution extends Attribution {
  savedAt: number;
}

// In-memory copy: the only one for a page without an embed_key, and the
// fallback when Web Storage is blocked (Safari ITP, sandboxed iframe) — same
// degrade-don't-throw rule as session/storage.ts.
const memory = new Map<string, string>();

// localStorage on this origin is shared by every operator's embed, so the
// entry is namespaced by embed_key. Without one there is nothing safe to
// namespace by: the value then lives in memory for this page load only.
function storageKey(embedKey: string | null): string {
  return embedKey === null ? STORAGE_KEY : `${STORAGE_KEY}:${embedKey}`;
}

function clean(raw: string | null): string | null {
  const value = raw?.trim() ?? '';
  return value !== '' && value.length <= MAX_LENGTH ? value : null;
}

function parse(raw: string | null): StoredAttribution | null {
  if (raw === null) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<StoredAttribution> | null;
    if (parsed === null || typeof parsed !== 'object' || typeof parsed.savedAt !== 'number') {
      return null;
    }
    const ref = typeof parsed.ref === 'string' ? clean(parsed.ref) : null;
    const clickId = typeof parsed.clickId === 'string' ? clean(parsed.clickId) : null;
    return ref === null && clickId === null ? null : { ref, clickId, savedAt: parsed.savedAt };
  } catch {
    return null;
  }
}

function readRaw(embedKey: string | null): string | null {
  const key = storageKey(embedKey);
  if (embedKey === null) return memory.get(key) ?? null;
  try {
    return window.localStorage.getItem(key) ?? memory.get(key) ?? null;
  } catch {
    return memory.get(key) ?? null;
  }
}

/** `ref` / `clickId` from a URL, or null when it carries neither. */
export function extractAttributionFromURL(url: string): Attribution | null {
  const params = new URL(url).searchParams;
  const ref = clean(params.get('ref'));
  const clickId = clean(params.get('clickId') ?? params.get('click_id'));
  return ref === null && clickId === null ? null : { ref, clickId };
}

export function saveAttribution(
  attribution: Attribution,
  embedKey: string | null,
  now: number = Date.now(),
): void {
  const key = storageKey(embedKey);
  const value = JSON.stringify({ ...attribution, savedAt: now } satisfies StoredAttribution);
  memory.set(key, value);
  if (embedKey === null) return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // storage unavailable — the in-memory copy already holds the value
  }
}

export function clearAttribution(embedKey: string | null): void {
  const key = storageKey(embedKey);
  memory.delete(key);
  if (embedKey === null) return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    // storage unavailable — the in-memory copy is already gone
  }
}

/** The stored attribution for this embed, or null when absent or expired. */
export function loadAttribution(
  embedKey: string | null,
  now: number = Date.now(),
): Attribution | null {
  const stored = parse(readRaw(embedKey));
  if (stored === null) return null;
  if (now - stored.savedAt > TTL_MS) {
    clearAttribution(embedKey);
    return null;
  }
  return { ref: stored.ref, clickId: stored.clickId };
}

/**
 * Boot-time capture. A URL that carries attribution replaces whatever was
 * stored (last click wins, and `ref` + `clickId` always travel as a pair); a
 * URL without it leaves an earlier click alone.
 */
export function captureAttributionFromURL(url: string, now: number = Date.now()): void {
  const attribution = extractAttributionFromURL(url);
  if (attribution === null) return;
  const embedKey = new URL(url).searchParams.get('embed_key') || null;
  saveAttribution(attribution, embedKey, now);
}
